// Hex rings, discs and the outline of any set of cells (R548).
//
// Several chart pieces need the same three answers: how far apart two cells
// are, which cells sit at exactly k rings, and where the border of a set of
// cells runs. Card 30 outlines a structure's cell plus its ring 1, card 46
// outlines the real reach of a Beacon ring by ring, and card 27 outlines a
// bank of deposit fields with their ring 1. One leaf owns all three, so no
// piece writes its own hex arithmetic.
//
// **THE COORDINATES.** `world.ts` numbers the map as an ODD-R offset grid:
// `cellCenter` pushes odd rows half a column to the right. The axial pair a
// distance needs is therefore
//
//   q = col − ((row − (row & 1)) >> 1),   r = row
//
// and the cube distance is the usual `(|dq| + |dq+dr| + |dr|) / 2`. The six
// axial steps below are the same six directions `world.ts` HEX_OFFSETS holds
// on both row parities, which is what makes the two agree.
//
// **THE DISTANCE IS GEOMETRY, NOT NAVIGATION.** `hexRing` and `cellsWithin`
// answer for a flat, empty board. A beam does not travel that way — a ridge
// stops it — so anything that must follow the water asks `beam-halo.ts`
// instead, which is the mirror of the server's own walk.

import { COLS, COL_PITCH, ROWS, cellCenter } from '../world';
import type { Loop } from './contours';
import { traceHexLoops } from './outlines';

/** The six axial steps, in the direction order of `world.ts` HEX_OFFSETS. */
const AXIAL: readonly (readonly [number, number])[] = [
  [1, 0], [0, 1], [-1, 1], [-1, 0], [0, -1], [1, -1],
];

/** The axial q of a cell on a grid `cols` wide. r is the row itself. */
function axialQ(col: number, row: number): number {
  return col - ((row - (row & 1)) >> 1);
}

/**
 * The number of rings between two cells, on a board with no rock in it.
 *
 * A negative or unreadable index answers `Infinity`, so a caller that
 * compares against a ring count never takes a stray index for a near
 * neighbour. The call takes the column count alone, so an index past the
 * end of the board reads as a row below it; a caller that can hold such an
 * index must range-check it first.
 */
export function hexDistance(a: number, b: number, cols = COLS): number {
  if (a < 0 || b < 0 || !Number.isFinite(a) || !Number.isFinite(b)) {
    return Number.POSITIVE_INFINITY;
  }
  const r0 = (a / cols) | 0, r1 = (b / cols) | 0;
  const q0 = axialQ(a % cols, r0), q1 = axialQ(b % cols, r1);
  const dq = q1 - q0, dr = r1 - r0;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

/** The cell index of an axial pair, or −1 off the grid. */
function cellOfAxial(q: number, r: number, cols: number, rows: number): number {
  if (r < 0 || r >= rows) return -1;
  const col = q + ((r - (r & 1)) >> 1);
  if (col < 0 || col >= cols) return -1;
  return r * cols + col;
}

/**
 * Every cell at EXACTLY `k` rings from `center`, in ascending cell index.
 *
 * The walk is the ring itself — six runs of `k` steps round the centre — so
 * the cost is 6k and not the area of the board. A cell that falls off the
 * grid is dropped, which is why a ring near the edge holds fewer than 6k
 * cells. Ring 0 is the centre; a negative `k` holds nothing.
 */
export function hexRing(center: number, k: number, cols = COLS, rows = ROWS): number[] {
  if (center < 0 || center >= cols * rows || k < 0) return [];
  if (k === 0) return [center];
  const col = center % cols, row = (center / cols) | 0;
  // Start k steps along direction 4 (straight up), then walk the six sides.
  let q = axialQ(col, row) + AXIAL[4][0] * k;
  let r = row + AXIAL[4][1] * k;
  const out: number[] = [];
  for (let side = 0; side < 6; side++) {
    for (let step = 0; step < k; step++) {
      const cell = cellOfAxial(q, r, cols, rows);
      if (cell >= 0) out.push(cell);
      q += AXIAL[side][0];
      r += AXIAL[side][1];
    }
  }
  out.sort((x, y) => x - y);
  return out;
}

/**
 * Every cell within `k` rings of `center`, the centre included, in ascending
 * cell index.
 *
 * This is the geometric disc. `beam-halo.ts` answers the other question —
 * which of those cells a beam actually reaches through the water.
 *
 * The walk goes row by row, and along each row by rising q, so the cells
 * come out in ascending index with no sort and one array. A Tender's aura
 * asks for its disc each frame (`aura-styles.ts auraCells`).
 */
export function cellsWithin(center: number, k: number, cols = COLS, rows = ROWS): number[] {
  if (center < 0 || center >= cols * rows || k < 0) return [];
  const row0 = (center / cols) | 0, q0 = axialQ(center % cols, row0);
  const out: number[] = [];
  for (let dr = -k; dr <= k; dr++) {
    const r = row0 + dr;
    if (r < 0 || r >= rows) continue;
    // The disc holds |dq| <= k, |dr| <= k and |dq + dr| <= k.
    const shift = (r - (r & 1)) >> 1;
    const lo = Math.max(q0 - k, q0 - dr - k) + shift, hi = Math.min(q0 + k, q0 - dr + k) + shift;
    for (let col = Math.max(lo, 0), end = Math.min(hi, cols - 1); col <= end; col++) out.push(r * cols + col);
  }
  return out;
}

/**
 * Every cell whose centre lies within `px` world px of the centre of
 * `center` (the distance equal to `px` counts), in ascending cell index.
 *
 * This is the server's test of a radius round a cell (`grid.near` of the
 * sim: `Dist2 <= r * r` from `CellCenter`), so a set the chart outlines
 * holds exactly the cells whose centre the rule reaches. Two neighbour
 * centres stand `COL_PITCH` apart, and a cell k rings out stands at least
 * `k * COL_PITCH * sqrt(3) / 2` away, so the ring count below holds every
 * cell the test can take.
 */
export function cellsWithinPx(center: number, px: number, cols = COLS, rows = ROWS): number[] {
  if (!(px >= 0)) return [];
  const k = Math.ceil(px / (COL_PITCH * (Math.sqrt(3) / 2))) + 1;
  const [cx, cy] = cellCenter(center, cols);
  const r2 = px * px;
  return cellsWithin(center, k, cols, rows).filter((c) => {
    const [x, y] = cellCenter(c, cols);
    const dx = x - cx, dy = y - cy;
    return dx * dx + dy * dy <= r2;
  });
}

/**
 * The hex-bound outline of any set of cells: the outer loops and the holes,
 * exactly as `traceHexLoops` returns them for a faction's ground.
 *
 * The set becomes a 0/1 mask and goes through `traceHexLoops`, so the corners
 * are the cells' own corners with no relax and no Chaikin: the chart draws
 * the hexes, not a smoothed coastline. A cell outside the grid is dropped.
 */
export function unionOutline(
  cells: Iterable<number>, cols = COLS, rows = ROWS,
): Loop[] {
  const mask = new Uint8Array(cols * rows);
  let any = false;
  for (const cell of cells) {
    if (cell >= 0 && cell < mask.length) { mask[cell] = 1; any = true; }
  }
  if (!any) return [];
  return traceHexLoops(mask, 1, cols, rows);
}
